import { useEffect, useState } from 'react';
import { ChevronDown, MessageCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface Props {
  /** Mandare il messaggio di benvenuto sul gruppo riusato? */
  value: boolean;
  onChange: (v: boolean) => void;
}

type Loaded =
  | { state: 'loading' }
  | { state: 'none' }            // nessun passo, o il passo non manda messaggi sul riuso
  | { state: 'template'; text: string };

/**
 * Scelta per singola offerta: quando alla firma si riusa un gruppo esistente,
 * chi chiude decide se far partire il messaggio di benvenuto dell'automazione.
 * Il testo NON e' nel codice: si legge dal passo "Crea gruppo WhatsApp cliente"
 * dell'automazione attiva (campo del passo o, se scelto, il testo dedicato al riuso).
 */
export function ReuseIntroChoice({ value, onChange }: Props) {
  const [loaded, setLoaded] = useState<Loaded>({ state: 'loading' });
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: wfs } = await supabase.from('automation_workflows').select('id').eq('is_active', true);
      const ids = (wfs ?? []).map((w: { id: string }) => w.id);
      if (cancelled) return;
      if (ids.length === 0) { setLoaded({ state: 'none' }); return; }
      const { data: steps } = await supabase
        .from('automation_workflow_steps')
        .select('config')
        .eq('step_type', 'create_whatsapp_group')
        .in('workflow_id', ids)
        .limit(1);
      if (cancelled) return;
      const cfg = ((steps ?? [])[0] as { config?: Record<string, unknown> } | undefined)?.config ?? {};
      const mode = (cfg.reuse_group_intro_mode as string | undefined) ?? 'same';
      const text = mode === 'none'
        ? ''
        : mode === 'custom'
          ? String(cfg.reuse_group_intro_template ?? '')
          : String(cfg.intro_message_template ?? '');
      setLoaded(text.trim() ? { state: 'template', text } : { state: 'none' });
    })();
    return () => { cancelled = true; };
  }, []);

  if (loaded.state === 'loading') return null;

  if (loaded.state === 'none') {
    return (
      <p className="mt-2 text-[11px] text-[#9B9A97]">
        L'automazione non manda messaggi quando si riusa un gruppo esistente.
      </p>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-[rgba(55,53,47,0.12)] dark:border-[#454B4E] px-3 py-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-[#9B9A97] text-[#2383E2] focus:ring-[#2383E2]"
        />
        <span className="flex-1 text-[12.5px] text-[#37352F] dark:text-white">
          Invia il messaggio di benvenuto nel gruppo
        </span>
        <MessageCircle className="h-3.5 w-3.5 text-[#9B9A97]" strokeWidth={1.5} />
      </label>
      <p className="mt-1 text-[11px] text-[#9B9A97]">
        {value
          ? 'Alla firma l\'automazione posta nel gruppo il testo qui sotto.'
          : 'Il gruppo viene riusato e i partecipanti mancanti aggiunti, ma senza messaggio.'}
      </p>
      <button
        type="button"
        onClick={() => setShowText((v) => !v)}
        className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#2383E2] hover:text-[#1a6cba]"
      >
        <ChevronDown className={`h-3 w-3 transition-transform ${showText ? 'rotate-180' : ''}`} strokeWidth={1.5} />
        {showText ? 'Nascondi il testo' : 'Vedi il testo'}
      </button>
      {showText && (
        <pre className="mt-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-black/[0.03] dark:bg-white/[0.04] px-2.5 py-2 font-sans text-[11.5px] leading-snug text-[#37352F] dark:text-white">
          {loaded.text}
        </pre>
      )}
      <p className="mt-1 text-[10.5px] text-[#9B9A97]">
        Il testo si modifica nel passo "Crea gruppo WhatsApp cliente" dell'automazione.
      </p>
    </div>
  );
}
